import { env, isEnvReady } from '@/lib/env'

/**
 * Registers `/firebase-messaging-sw.js` with the Firebase web config
 * encoded into the registration URL as query parameters — service
 * workers can't read `import.meta.env`, so this is the canonical way to
 * pipe public config into the SW at install time.
 *
 * Best-effort: a registration failure is non-fatal (push degrades, the
 * SPA keeps working). Skipped entirely when env isn't ready or when the
 * Firebase project values are missing — pointless to install a SW that
 * can't reach FCM.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined') return null
  if (!('serviceWorker' in navigator)) return null
  if (!isEnvReady()) return null

  const e = env()
  const cfg = {
    apiKey: e.VITE_FIREBASE_API_KEY,
    authDomain: e.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: e.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: e.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: e.VITE_FIREBASE_APP_ID,
  }
  // No point installing a SW that can't reach Firebase. The
  // WebPushSubscribeCard will surface a "not configured" notice instead.
  if (Object.values(cfg).some((v) => !v)) return null

  const swUrl = `/firebase-messaging-sw.js?${new URLSearchParams(
    cfg as Record<string, string>,
  ).toString()}`

  try {
    const reg = await navigator.serviceWorker.register(swUrl, { scope: '/' })
    await reg.update().catch(() => undefined)
    return reg
  } catch (err) {
    console.warn('[sw] firebase-messaging-sw registration failed', err)
    return null
  }
}
